// ChatsHistoryV2 — left column for the V3 data-explorer layout.
// Presentational only: takes a grouped list of sessions, an active id, and
// callbacks. The host wires it to the actual session store.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {
    MoreHoriz as MoreIcon,
    EditOutlined as RenameIcon,
    DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { PlusIcon, SearchIcon, CloseIcon } from './icons';
import { ChatSessionGroup } from '@lib/hooks';
import type { ChatSession } from '@lib/models/ChatSession';
import type { ChatSessionListItem } from '@lib/hooks';


export interface ChatsHistoryV2Item {
    id: string;
    title: string;
    /** e.g. "agora", "2h", "1d", "5d", "1sem" */
    time?: string;
    snippet?: string;
    /**
     * Optional session kind. The host distinguishes between regular
     * data-explorer chats and composer sessions (those that produced
     * a dashboard) so the row can render a small visual cue and the
     * click handler can route to the right view.
     */
    kind?: 'chat' | 'composer';
    /**
     * When true, animate `title` char-by-char whenever it changes.
     * Host marks the "current working session" with this so the
     * generated overview reveals itself as if being typed live.
     */
    typewriter?: boolean;
}

/**
 * Renders `text` immediately on mount, but animates char-by-char
 * whenever the prop subsequently changes. Used by the active session
 * row so a freshly generated overview reveals itself like a typewriter
 * while initial renders (refresh, re-mount with stable title) stay
 * snappy.
 */
const TypewriterText: React.FC<{ text: string; charsPerSecond?: number }> = ({
    text,
    charsPerSecond = 20,
}) => {
    const [displayed, setDisplayed] = useState(text);
    const prevTextRef = useRef(text);

    useEffect(() => {
        if (text === prevTextRef.current) return;
        prevTextRef.current = text;

        // Animate from 0 → full text. Short titles need a higher
        // floor so the effect is visible at all; otherwise a 6-char
        // overview finishes in ~150ms which feels like a flicker.
        const interval = Math.max(20, 1000 / charsPerSecond);
        setDisplayed('-');
        let i = 0;
        const timer = window.setInterval(() => {
            i += 1;
            setDisplayed(text.slice(0, i));
            if (i >= text.length) window.clearInterval(timer);
        }, interval);
        return () => window.clearInterval(timer);
    }, [text, charsPerSecond]);

    return <>{displayed}</>;
};


export interface ChatsHistoryV2Props {
    groups: ChatSessionGroup[];
    newActiveSession?: ChatSession,
    activeId?: string;
    onSelect?: (item: ChatSessionListItem) => void;
    onNewSession?: () => void;
    /**
     * Rename a session. Receives the new title. The host should patch
     * the session's `overview` (or whatever it shows as the title) and
     * refresh the list.
     */
    onRename?: (id: string, newTitle: string) => void | Promise<void>;
    /** Delete a session. The host should confirm if it wants extra friction beyond the built-in dialog. */
    onDelete?: (id: string) => void | Promise<void>;
    /** Footer slot, typically a small avatar + name. */
    footer?: React.ReactNode;
    /** Header brand mark / app name. */
    header?: React.ReactNode;
    /** Search box label override. */
    searchPlaceholder?: string;
    /** Total count override; defaults to flat sum of items. */
    totalCount?: number;
}

const PANEL_WIDTH = '15vw';

export const ChatsHistoryV2: React.FC<ChatsHistoryV2Props> = ({
    groups,
    newActiveSession,
    activeId,
    onSelect,
    onNewSession,
    onRename,
    onDelete,
    footer,
    header,
    searchPlaceholder,
    totalCount,
}) => {
    const [query, setQuery] = useState('');
    const computedTotal = useMemo(() => groups.reduce((n, g) => n + g.items.length, 0), [groups]);
    const total = totalCount ?? computedTotal;
    const placeholder = searchPlaceholder ?? `Buscar...`;

    // Per-row action menu anchor + which item the menu is for.
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; item: ChatSessionListItem } | null>(null);
    const closeMenu = () => setMenuAnchor(null);

    // Inline rename state: the id of the row currently being edited and
    // the in-progress draft title.
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState('');
    const renameInputRef = useRef<HTMLTextAreaElement | null>(null);
    // Guard onBlur: when the input first mounts, MUI Menu's restoreFocus
    // pulls focus back to the (now-hidden) trigger button. That fires a
    // premature blur that would auto-cancel the rename. We ignore blur
    // events until the input has actually settled (received its first
    // focus a beat after mount).
    const ignoreNextBlurRef = useRef(false);

    // Resize the textarea to fit its content (capped) so a 2-line title
    // stays fully visible while editing.
    const fitTextarea = (el: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
    };

    useEffect(() => {
        if (renamingId == null) return;
        ignoreNextBlurRef.current = true;
        const t = window.setTimeout(() => {
            const el = renameInputRef.current;
            if (el) {
                fitTextarea(el);
                el.focus();
                el.select();
            }
            ignoreNextBlurRef.current = false;
        }, 80);
        return () => window.clearTimeout(t);
    }, [renamingId]);

    // Delete-confirm dialog target.
    const [deleteTarget, setDeleteTarget] = useState<ChatsHistoryV2Item | null>(null);

    const startRename = (item: any) => {
        setRenamingId(item.id);
        setRenameDraft(item.title);
        closeMenu();
    };
    const cancelRename = () => {
        setRenamingId(null);
        setRenameDraft('');
    };
    const commitRename = (id: string) => {
        Promise.resolve(onRename?.(id, renameDraft)).finally(cancelRename);
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        Promise.resolve(onDelete?.(target.id));
    };

    const filteredGroups = useMemo(() => {
        let activeGroup = newActiveSession ? {
            items: [{
                id: newActiveSession.id,
                title: newActiveSession?.overview?.trim() !== "" ? newActiveSession?.overview :  "Nova",
                timeLabel: "Agora",
                kind: "typewritter",
            }],
            label: 'agora'
        } as ChatSessionGroup : null;

        let allGroups = groups;
        if (activeGroup) {
            allGroups = [activeGroup, ...allGroups];
        }

        if (!query) return allGroups;

        const q = query.toLowerCase();
        return groups
            .map((g) => ({ ...g, items: g.items.filter((it) => it.title.toLowerCase().includes(q)) }))
            .filter((g) => g.items.length > 0)
    }, [groups, query, newActiveSession]);

    return (
        <Box
            component="aside"
            sx={(theme) => ({
                maxWidth: PANEL_WIDTH,
                flexShrink: 0,
                backgroundColor: theme.palette.background.paper,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            })}
        >
            {/* Header */}
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                {header && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                        <Box sx={{ flex: 1 }}>{header}</Box>
                        {onNewSession && (
                            <Tooltip title="Nova conversa">
                                <IconButton size="small" onClick={onNewSession} sx={(theme) => ({ color: theme.palette.text.secondary, p: 0.5 })}>
                                    <PlusIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        backgroundColor: theme.palette.surface.raised,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: `${theme.shape.radiusSm}px`,
                        px: 1.25,
                        py: 0.5,
                    })}
                >
                    <Box sx={(theme) => ({ color: theme.palette.text.disabled, display: 'inline-flex' })}><SearchIcon /></Box>
                    <Box
                        component="input"
                        value={query}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                        placeholder={placeholder}
                        sx={(theme) => ({
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            color: theme.palette.text.primary,
                            fontSize: 12,
                            fontFamily: theme.typography.fontFamily,
                        })}
                    />
                    {query && (
                        <IconButton size="small" onClick={() => setQuery('')} sx={(theme) => ({ color: theme.palette.text.disabled, p: 0.25 })}>
                            <CloseIcon size={11} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Groups */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 1, pb: 1.5 }}>
                {filteredGroups.map((group) => (
                    <Box key={group.label} sx={{ mt: 1.25 }}>
                        <Typography
                            sx={(theme) => ({
                                fontSize: 10,
                                color: theme.palette.text.disabled,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                px: 0.75,
                                py: 0.5,
                            })}
                        >
                            {group.label}
                        </Typography>
                        {group.items.map((it) => {
                            if (!it) null;

                            const active = it.id === activeId;
                            const isRenaming = renamingId === it.id;
                            const showActions = (onRename || onDelete) && !isRenaming;
                            return (
                                <Box
                                    key={it.id}
                                    sx={(theme) => ({
                                        position: 'relative',
                                        backgroundColor: active ? theme.palette.surface.hover : 'transparent',
                                        borderRadius: `${theme.shape.radiusSm}px`,
                                        borderLeft: `2px solid ${active ? theme.palette.primary.main : 'transparent'}`,
                                        mt: 0.25,
                                        transition: 'background-color .12s',
                                        '&:hover': {
                                            backgroundColor: active ? theme.palette.surface.hover : theme.palette.surface.raised,
                                        },
                                        '&:hover .v2chh-actions, &:focus-within .v2chh-actions': {
                                            opacity: 1,
                                        },
                                    })}
                                >
                                    <Box
                                        role={isRenaming ? undefined : 'button'}
                                        tabIndex={isRenaming ? undefined : 0}
                                        onClick={isRenaming ? undefined : () => onSelect?.(it)}
                                        onKeyDown={
                                            isRenaming
                                                ? undefined
                                                : (e) => { if (e.key === 'Enter') onSelect?.(it); }
                                        }
                                        sx={{
                                            width: '100%',
                                            textAlign: 'left',
                                            cursor: isRenaming ? 'default' : 'pointer',
                                            px: 0.5,
                                            py: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.25,
                                        }}
                                    >
                                        {isRenaming ? (
                                            <Box
                                                component="textarea"
                                                ref={(el: HTMLTextAreaElement | null) => {
                                                    renameInputRef.current = el;
                                                }}
                                                rows={1}
                                                value={renameDraft}
                                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                                    setRenameDraft(e.target.value);
                                                    fitTextarea(e.currentTarget);
                                                }}
                                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        commitRename(it.id);
                                                    } else if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        cancelRename();
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (ignoreNextBlurRef.current) return;
                                                    commitRename(it.id);
                                                }}
                                                sx={(theme) => ({
                                                    width: '100%',
                                                    background: theme.palette.surface.raised,
                                                    border: `1px solid ${theme.palette.primary.main}`,
                                                    borderRadius: `${theme.shape.radiusSm}px`,
                                                    color: theme.palette.text.primary,
                                                    fontSize: 12,
                                                    lineHeight: 1.4,
                                                    fontFamily: theme.typography.fontFamily,
                                                    px: 0.75,
                                                    py: 0.5,
                                                    outline: 'none',
                                                    resize: 'none',
                                                    overflow: 'hidden',
                                                    minHeight: 28,
                                                    maxHeight: 80,
                                                    boxSizing: 'border-box',
                                                })}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                                                {it.kind === 'composer' && (
                                                    <Tooltip title="Sessão do composer" placement="top">
                                                        <Box
                                                            component="span"
                                                            aria-label="Sessão do composer"
                                                            sx={(theme) => ({
                                                                flexShrink: 0,
                                                                width: 14,
                                                                height: 14,
                                                                borderRadius: '4px',
                                                                background: theme.palette.primary.softBg,
                                                                color: theme.palette.primary.main,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: 10,
                                                                lineHeight: 1,
                                                            })}
                                                        >
                                                            ◧
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                <Typography
                                                    sx={(theme) => ({
                                                        fontSize: 12,
                                                        color: theme.palette.text.primary,
                                                        fontWeight: active ? 500 : 400,
                                                        whiteSpace: 'wrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '100%',
                                                        maxHeight: active ? '72px' : '37px',
                                                        // Multi-line titles inherit the document's
                                                        // default text-align which renders weirdly
                                                        // here (looks justified — last line ends up
                                                        // visually offset from the first). Pin to
                                                        // left so wrapped titles read cleanly.
                                                        textAlign: 'left',
                                                    })}
                                                >
                                                    {it.kind == "typewritter" ? <TypewriterText text={it.title} charsPerSecond={30} /> : it.title}
                                                </Typography>
                                            </Box>
                                        )}
                                        {it.updatedAt && !isRenaming && (
                                            <Typography
                                                sx={(theme) => ({
                                                    fontSize: 10.5,
                                                    color: theme.palette.text.disabled,
                                                    fontFamily: theme.typography.fontFamilyMono,
                                                })}
                                            >
                                                {it.timeLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                    {showActions && (
                                        <Box
                                            className="v2chh-actions"
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: 4,
                                                transform: 'translateY(-50%)',
                                                opacity: 0,
                                                transition: 'opacity .12s',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                aria-label="Mais ações"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMenuAnchor({ el: e.currentTarget, item: it });
                                                }}
                                                sx={(theme) => ({
                                                    color: theme.palette.text.secondary,
                                                    p: 0.5,
                                                    '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.surface.hover },
                                                })}
                                            >
                                                <MoreIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
                {filteredGroups.length === 0 && (
                    <Typography sx={(theme) => ({ px: 1.5, py: 2, fontSize: 11, color: theme.palette.text.disabled })}>
                        Nenhuma conversa.
                    </Typography>
                )}
            </Box>

            {footer && (
                <Box
                    sx={(theme) => ({
                        px: 1.5,
                        py: 1.25,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    })}
                >
                    {footer}
                </Box>
            )}

            {/* Per-row action menu */}
            <Menu
                open={!!menuAnchor}
                anchorEl={menuAnchor?.el ?? null}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            backgroundColor: theme.palette.surface.raised,
                            border: `1px solid ${theme.palette.divider}`,
                            mt: 0.5,
                            minWidth: 160,
                        }),
                    },
                }}
            >
                {onRename && (
                    <MenuItem
                        onClick={() => {
                            if (menuAnchor) startRename(menuAnchor.item);
                        }}
                        sx={(theme) => ({ fontSize: 12, color: theme.palette.text.primary })}
                    >
                        <ListItemIcon sx={(theme) => ({ color: theme.palette.text.secondary, minWidth: 28 })}>
                            <RenameIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Renomear"
                            primaryTypographyProps={{ fontSize: 12 }}
                        />
                    </MenuItem>
                )}
                {onDelete && (
                    <MenuItem
                        onClick={() => {
                            if (menuAnchor) {
                                setDeleteTarget(menuAnchor.item as any);
                                closeMenu();
                            }
                        }}
                        sx={(theme) => ({ fontSize: 12, color: theme.palette.secondary.main })}
                    >
                        <ListItemIcon sx={(theme) => ({ color: theme.palette.secondary.main, minWidth: 28 })}>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Excluir"
                            primaryTypographyProps={{ fontSize: 12 }}
                        />
                    </MenuItem>
                )}
            </Menu>

            {/* Delete confirmation */}
            <Dialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            minWidth: 360,
                        }),
                    },
                }}
            >
                <DialogTitle sx={(theme) => ({ fontSize: 14, color: theme.palette.text.primary })}>
                    Excluir conversa?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={(theme) => ({ fontSize: 12, color: theme.palette.text.secondary })}>
                        Tem certeza que deseja excluir{' '}
                        <Box component="strong" sx={(theme) => ({ color: theme.palette.text.primary })}>
                            {deleteTarget?.title}
                        </Box>
                        ? Esta ação não pode ser desfeita.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteTarget(null)}
                        sx={(theme) => ({ color: theme.palette.text.secondary, fontSize: 12 })}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        sx={(theme) => ({
                            color: theme.palette.secondary.main,
                            fontSize: 12,
                            fontWeight: 500,
                            '&:hover': { backgroundColor: 'rgba(217,122,122,0.08)' },
                        })}
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

ChatsHistoryV2.displayName = 'ChatsHistoryV2';
